export type TailwindColor =
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose'
  | 'slate'
  | 'gray'
  | 'zinc'
  | 'neutral'
  | 'stone';

export type BtnColors =
  | 'primary'
  | 'secondary'
  | 'cancel'
  | 'transparent'
  | 'disabled'
  | 'danger'
  | 'success'
  | 'notice'
  | 'warning';

export type AlertType = 'success' | 'notice' | 'warning' | 'danger';

export const itemsColors = {
  darkGrey: '#4e4c4c',
  red: '#f44336',
  pink: '#e91e63',
  purple: '#9c27b0',
  deepPurple: '#673ab7',
  indigo: '#3f51b5',
  blue: '#2196f3',
  lightBlue: '#03a9f4',
  cyan: '#00bcd4',
  teal: '#009688',
  green: '#4caf50',
  lightGreen: '#8bc34a',
  amber: '#ffc107',
  orange: '#ff9800',
  deepOrange: '#ff5722',
  deepPink: '#ff1493',
  darkCyan: '#008b8b'
} as const;

export const pickupColor = '#984623';
export const deliveryColor = '#083b43';

export const itemColorKeys: ItemColorKey[] = Object.keys(itemsColors) as ItemColorKey[];

export type ItemColorKey = keyof typeof itemsColors;
export type ItemColorValue = (typeof itemsColors)[ItemColorKey];

/**
 * Maps a string to a predefined hex color value using a deterministic hash function.
 *
 * This function uses a simple hash algorithm to convert the input string into a numerical
 * hash, then maps that hash to one of the predefined hex color values using modulo
 * arithmetic. The same input string will always return the same hex color.
 *
 * @remarks
 *   - The function is deterministic: identical inputs produce identical outputs
 *   - Empty strings will always map to the first color in the itemsColors object
 *   - The distribution of colors depends on the hash function and may not be perfectly uniform
 *   - Color assignment is based on string content, not string length or other factors
 *
 * @example
 *   ```typescript
 *   stringToColor("hello");     // Always returns the same hex color, e.g., '#2196f3'
 *   stringToColor("world");     // Always returns the same hex color, e.g., '#f44336'
 *   stringToColor("hello");     // Returns '#2196f3' again (deterministic)
 *   stringToColor("");          // Handles empty strings, returns '#4e4c4c' (first color)
 *   ```;
 *
 * @param input - The string to map to a color
 * @returns A hex color value from the predefined itemsColors (e.g., '#4e4c4c')
 */
export function stringToColor(input: string | null | undefined): string {
  const safeInput = input ?? '';
  // Simple hash function - sum character codes
  let hash = 0;
  for (let i = 0; i < safeInput.length; i++) {
    hash += safeInput.charCodeAt(i);
  }

  // Map hash to color index
  const colorIndex = hash % itemColorKeys.length;
  const colorKey = itemColorKeys[colorIndex];
  return itemsColors[colorKey];
}

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/**
 * The color a route is drawn in: its own `color` when it carries a valid `#RRGGBB`, otherwise
 * one derived from its uid. The shape is re-checked here because the value ends up in inline
 * styles and canvas fills, where a malformed string silently renders as nothing.
 */
export function routeColor(uid: string, color: string | null | undefined): string {
  return color != null && HEX_COLOR.test(color) ? color : stringToColor(uid);
}
